/**
 * KB PDF ingest — the ONE multipart call in the admin feature.
 *
 * The vendored JSON `api` client (configureApi) deliberately models only JSON
 * request bodies, so a `multipart/form-data` PDF upload can't go through it. The
 * PR-creation step (action=pr) IS JSON and goes through `api.post` via the hook;
 * only the binary upload below needs raw fetch.
 *
 * AUTH SEAM (important — re-read rebuild-lessons.md #2 before changing this):
 *   We reuse the shell's SINGLE token resolver `acquireToken` from
 *   `@/auth/msal` — the exact same function the vendored api client was wired
 *   with in src/auth/resolver.ts. This is NOT per-page MSAL: there is no
 *   `new PublicClientApplication`, no `/api/chat/config` fetch, no
 *   sessionStorage token key. The shell owns the one MSAL instance; we only ask
 *   it for a bearer. If the token is unavailable we throw — we never send an
 *   unauthenticated request (which would 401 and bounce the user). The admin
 *   gate (AdminGuard) has already confirmed the caller is an admin before this
 *   page renders, so a missing token here is a real auth failure, surfaced to
 *   the user rather than silently swallowed.
 *
 * At merge into andavo-dev this single import re-points to the Auth0 token
 * getter alongside the resolver swap; nothing else changes.
 */
import { acquireToken } from '@/auth/msal';
import { adminUrl, getExpertsScopes } from '../admin-config';
import type { KbIngestResult } from '../types';

async function bearer(): Promise<string> {
  // EXPERTS-audience token via the scope-parameterized acquireToken (same seam
  // as chat; the experts backend serves /api/admin/*). NOT the parameterless form.
  const token = await acquireToken(getExpertsScopes());
  if (!token) {
    throw new Error(
      'No access token available — sign in again (your session may have expired).',
    );
  }
  return token;
}

/** Upload a PDF; the backend returns generated markdown + slug for review. */
export async function uploadKbPdf(file: File): Promise<KbIngestResult> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(adminUrl('/api/admin/kb/upload'), {
    method: 'POST',
    headers: { Authorization: `Bearer ${await bearer()}` },
    body: form,
  });
  const data = (await res
    .json()
    .catch(() => ({}))) as Partial<KbIngestResult> & {
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `Upload failed (HTTP ${res.status})`);
  }
  return { markdown: data.markdown ?? '', slug: data.slug ?? '' };
}
